import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useFetch, useClientsList } from "@/lib/hooks";
import { PageHeader, Panel } from "@/components/core/AppLayout";
import { CoreCalendar } from "@/components/calendar/CoreCalendar";
import { useAttachmentCounts } from "@/components/core/AttachmentsPanel";
import { PersonalEventPanel, COLORS } from "@/components/notes/PersonalEventPanel";

const newDraft = (start, end, allDay) => ({ titolo: "", inizio: start.toISOString(), fine: end.toISOString(), all_day: allDay, colore: COLORS[0], note_html: "" });

const SearchResults = ({ q, onOpen }) => {
  const { data } = useFetch(`/me/eventi?q=${encodeURIComponent(q)}`);
  return (
    <Panel className="p-4 mb-4" data-testid="pcal-search-results">
      {data && !data.length && <div className="text-sm text-slate-600">Nessun evento trovato per «{q}».</div>}
      {(data || []).map((e) => (
        <button key={e.id} onClick={() => onOpen(e)} className="w-full text-left flex items-start gap-3 rounded-xl px-3 py-2 hover:bg-[#A6DAFC]" data-testid={`pcal-result-${e.id}`}>
          <span className="h-3 w-3 rounded-full mt-1.5 shrink-0" style={{ background: e.colore }} />
          <div className="min-w-0"><div className="font-bold text-[#0A1128]">{e.titolo} <span className="font-normal text-xs text-slate-600">· {fmtDateTime(e.inizio)}</span></div>
            {e.note_text && <div className="text-sm text-slate-600 truncate">{e.note_text}</div>}</div>
        </button>
      ))}
    </Panel>
  );
};

export default function MyCalendarPage() {
  const { data: events, setData } = useFetch("/me/eventi");
  const { data: clients } = useClientsList();
  const [panel, setPanel] = useState(null);
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [sp, setSp] = useSearchParams();
  const [focus, setFocus] = useState(null);

  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    const id = sp.get("evento");
    if (!id) return;
    api.get(`/me/eventi/${id}`).then((r) => { setPanel({ key: id, initial: r.data }); setFocus(r.data.inizio); }).catch(() => toast.error("Evento non trovato"));
    setSp({}, { replace: true });
  }, [sp, setSp]);

  const open = (e) => { setPanel({ key: `${e.id}-${Date.now()}`, initial: e }); setFocus(e.inizio); };
  const upsert = (ev) => setData((p) => [...(p || []).filter((x) => x.id !== ev.id), ev]);
  const reschedule = async (info) => {
    const ev = info.event;
    const end = ev.end || new Date(ev.start.getTime() + (ev.allDay ? 86400e3 : 3600e3));
    try { upsert((await api.patch(`/me/eventi/${ev.id}/orario`, { inizio: ev.start.toISOString(), fine: end.toISOString(), all_day: ev.allDay })).data); toast.success("Evento spostato"); }
    catch (e) { info.revert(); toast.error(errMsg(e)); }
  };
  const attCounts = useAttachmentCounts("event", [(events || []).length]);
  const fc = (events || []).map((e) => ({ id: e.id, title: attCounts[e.id] ? `${e.titolo} · 📎 ${attCounts[e.id]}` : e.titolo, start: e.inizio, end: e.fine, allDay: e.all_day, backgroundColor: e.colore, borderColor: e.colore, textColor: "#fff", extendedProps: { ev: e } }));
  const now = new Date();

  return (
    <div data-testid="my-calendar-page">
      <PageHeader eyebrow={<span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />Visibile solo a te</span>} title="Il mio calendario" subtitle="Eventi personali e appunti privati. Gli appuntamenti di lavoro restano nel Calendario." />
      <div className="flex items-center gap-3 mb-4">
        <div className="relative w-[380px]"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nei titoli e negli appunti" className="rounded-full pl-9" data-testid="pcal-search" /></div>
        <Button onClick={() => setPanel({ key: `new-${Date.now()}`, initial: newDraft(new Date(now.setMinutes(0, 0, 0) + 3600e3), new Date(now.getTime() + 7200e3), false) })} className="ml-auto rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="pcal-new-event"><Plus className="h-4 w-4 mr-1" />Nuovo evento</Button>
      </div>
      {query && <SearchResults q={query} onOpen={open} />}
      <Panel className="p-5">
        <CoreCalendar events={fc} initialView="timeGridWeek" editable focusDate={focus} onEventDrop={reschedule} onEventResize={reschedule}
          onEventClick={(ev) => open(ev.extendedProps.ev)} onSelect={(info) => setPanel({ key: `new-${Date.now()}`, initial: newDraft(info.start, info.end, info.allDay) })} />
      </Panel>
      <PersonalEventPanel panel={panel} onClose={() => setPanel(null)} clients={clients} onChanged={upsert} onDeleted={(id) => setData((p) => p.filter((x) => x.id !== id))} />
    </div>
  );
}
